import React, {
    useContext,
    useState,
    useMemo,
    createContext,
    useEffect,
} from "react";
import { useMsal } from "@azure/msal-react";
import {
    AdministratorTachContextData,
    AdministratorTachProviderProps,
    DacPermission,
    Processo,
    Subprocesso,
} from "./interfaces/IAdministratorTachContext";
import { IPagination } from "../components/IPagination";
import AdminTachService from "../service/Docente/AdminTachService";
import GenericUtils from "../utils/GenericUtils";
import { IFilter } from "./interfaces/IFilterDataContext";
import {
    initialFilter,
    initialPagination,
} from "./TachContext/initialFilterData";

export const AdministratorTachContext =
    createContext<AdministratorTachContextData>(
        {} as AdministratorTachContextData
    );

export function AdministratorTachProvider({
    children,
}: AdministratorTachProviderProps) {
    const [subprocess, setSubprocess] = useState<Subprocesso[]>([]);
    const isCreatedSubprocess = !!subprocess.length;
    const [openModalData, setOpenModalData] = useState<boolean>(false);
    const [pagination, setPagination] =
        useState<IPagination>(initialPagination);
    const [dacPermission, setDacPermission] = useState<DacPermission>({
        hasPermission: false,
        message: "",
        title: "",
    });

    const [isPendingProcess, setIsPendingProcess] = useState<boolean>(true);

    const { instance } = useMsal();

    const getSubprocessos = async (
        filterPage: IFilter,
        paginationFilter: IPagination
    ) => {
        setSubprocess([]);

        const response = await AdminTachService.getSubprocess(
            filterPage,
            paginationFilter,
            instance
        );

        if (
            GenericUtils.isSuccessStatus(response) &&
            response.data &&
            response.data.itens
        ) {
            if (response.data.itens.length) {
                const newSubprocess = response.data.itens;

                const responsePagination: IPagination =
                    response.data.meta || ({} as IPagination);

                const newPagination: IPagination = {
                    ...pagination,
                    actualPage: responsePagination.actualPage,
                    totalPages: responsePagination.totalPages,
                    totalRecords: responsePagination.totalRecords,
                };

                setSubprocess(newSubprocess);
                setPagination(newPagination);
            } else {
                setPagination(initialPagination);
            }
        }
    };

    const getPendingProcess = async () => {
        const response = await AdminTachService.getProcess(instance);

        if (
            GenericUtils.isSuccessStatus(response) &&
            response.data &&
            response.data.itens.length
        ) {
            const newProcess: Processo[] = response.data.itens;

            const hasPendingProcess = newProcess.some(
                (process) => process.status === "PENDING"
            );

            return setIsPendingProcess(hasPendingProcess);
        }

        return setIsPendingProcess(false);
    };

    const getDacPermission = async (): Promise<void> => {
        const response = await AdminTachService.getDacPermission(instance);

        if (GenericUtils.isSuccessStatus(response)) {
            setDacPermission({
                hasPermission: true,
                message: "",
                title: "",
            });
            return;
        }

        setDacPermission({
            hasPermission: false,
            message: response.data.details,
            title: response.data.message,
        });
    };

    const handleGenerateTach = async () => {};

    useEffect(() => {
        getSubprocessos(initialFilter, initialPagination);
        getPendingProcess();
        getDacPermission();
    }, []);

    const contextValue = useMemo(
        () => ({
            getSubprocessos,
            subprocess,
            isCreatedSubprocess,
            handleGenerateTach,
            openModalData,
            setOpenModalData,
            pagination,
            setPagination,
            isPendingProcess,
            setIsPendingProcess,
            getPendingProcess,
            dacPermission,
        }),
        [
            getSubprocessos,
            subprocess,
            isCreatedSubprocess,
            handleGenerateTach,
            openModalData,
            setOpenModalData,
            pagination,
            setPagination,
            isPendingProcess,
            setIsPendingProcess,
            getPendingProcess,
            dacPermission,
        ]
    );

    return (
        <AdministratorTachContext.Provider value={contextValue}>
            {children}
        </AdministratorTachContext.Provider>
    );
}

AdministratorTachContext.displayName = "AdministratorTach";

export function useAdministratorTach() {
    return useContext(AdministratorTachContext);
}
